import cx from "classnames";
import { graphql, PageProps } from "gatsby";
import { getSrc } from "gatsby-plugin-image";
import React from "react";

import type {
  HomeFieldsFragment,
  HomeQueryV2Query,
} from "../../../graphql-types";
import { BenefitImageList } from "../../components/BenefitImageList";
import Certificates from "../../components/Certificates";
import Disclaimer from "../../components/Disclaimer";
import ExternalLink from "../../components/ExternalLink";
import Facts from "../../components/Facts";
import Image from "../../components/GatsbyImage";
import { default as GlobalHead } from "../../components/head";
import Heading from "../../components/Heading";
import InternalLink from "../../components/InternalLink";
import Layout from "../../components/layout";
import PromoCard from "../../components/PromoCard";
import PromoCardGrid from "../../components/PromoCardGrid";
import Summary from "../../components/Summary";
import TruncatedMessage from "../../components/TruncatedMessage";
import { VideoPlayer } from "../../components/VideoPlayer";
import { useLexicon } from "../../context/LexiconContext";
import HomeWidget from "../sustainability/components/HomeWidget";
import HomeInsta from "./HomeInsta";
import HomeMessage from "./HomeMessage";
import HomeVideo from "./HomeVideo";

export type Props = Pick<PageProps<HomeQueryV2Query>, "data">;

const Home = ({ data }: Props) => {
  const lexicon = useLexicon();
  const { allSustainabilityCategory, website } = data;
  return (
    <Layout
      absolutePath={website?.absolutePath as string}
      home={website as HomeFieldsFragment}
      metaDescription={website?.brand.description as string}
      pageTitle={website?.homepageTitle}
      pageType="home"
      fullWidth
    >
      {website?.coreMessage?.heroVideo?.cdnVideo ? (
        <div className="max-w-container mx-auto">
          <VideoPlayer
            data-testid="hero-video"
            data-cy="hero-video"
            poster={website.coreMessage.heroVideo.cdnVideo.posterPath}
            path={website.coreMessage.heroVideo.cdnVideo.path}
          />
        </div>
      ) : (
        <div data-cy="hero-image" className="max-w-container mx-auto">
          {website?.coreMessage?.heroImageLink?.path ? (
            <InternalLink href={website?.coreMessage?.heroImageLink.path}>
              <Image
                image={website?.coreMessage?.hero?.gatsbyImageData}
                alt={website?.coreMessage?.hero?.description as string}
                loading="eager"
              />
              <span className="sr-only">
                {website?.coreMessage?.heroImageLink.title}
              </span>
            </InternalLink>
          ) : (
            <Image
              image={website?.coreMessage?.hero?.gatsbyImageData}
              alt={website?.coreMessage?.hero?.description as string}
              loading="eager"
            />
          )}
        </div>
      )}
      <div className="max-w-container p-12 mx-auto md:p-16">
        <Heading data-cy="primary-heading" className="text-[2rem]" as="h1">
          {website?.coreMessage?.title}
        </Heading>
      </div>

      {website?.coreMessage?.summary && (
        <Summary summary={website?.coreMessage?.summary} />
      )}
      {website?.coreMessage?.benefitImages && (
        <BenefitImageList images={website?.coreMessage?.benefitImages as []} />
      )}
      {website?.coreMessage?.benefitsDisclaimer && (
        <div className="max-w-lg px-6 py-8 lg:px-0 md:mx-auto">
          <Disclaimer>{website?.coreMessage?.benefitsDisclaimer}</Disclaimer>
        </div>
      )}
      {website?.coreMessage?.main && (
        <TruncatedMessage
          linkPath={website.coreMessage.path}
          linkTitle={website.coreMessage.title}
          rawTruncated={website.coreMessage.main.rawTruncated}
        />
      )}
      <Facts facts={website?.coreMessage?.facts} />
      {website?.sustainabilityWidgetWebsite &&
        allSustainabilityCategory?.nodes.length === 3 && (
          <HomeWidget
            categories={allSustainabilityCategory.nodes}
            widget={website?.sustainabilityWidgetWebsite}
          />
        )}
      {website?.messages && website?.messages?.length > 0 && (
        <section
          data-cy="products"
          className="pb-20 bg-gray-100 products-updated"
        >
          <Heading as="h2" className="p-10 text-2xl md:text-[1.625rem] md:p-14">
            {lexicon.products}
          </Heading>
          <PromoCardGrid>
            {website?.messages.map(({ hero, path, teaserImage, title }) => (
              <PromoCard
                key={path}
                asset={teaserImage || hero}
                href={path}
                title={title}
                imagePaddingY="0"
              />
            ))}
          </PromoCardGrid>
        </section>
      )}
      <div className="max-w-container px-4 mx-auto">
        <HomeInsta />
      </div>
      {website?.secondaryMessage && (
        <>
          <section className="px-6 py-14 bg-gray-100 secondary-message-teaser">
            <div className="max-w-container mx-auto block bg-white rounded-lg lg:flex drop-shadow-[0_10px_5px_rgba(0,0,0,0.1)]">
              <div className="lg:w-2/3">
                {website?.secondaryMessage?.heroImageLink?.path ? (
                  <InternalLink
                    href={website?.secondaryMessage?.heroImageLink.path}
                  >
                    <Image
                      className="rounded-t-lg lg:rounded-l-lg lg:rounded-tr-none"
                      image={
                        website?.secondaryMessage?.heroConstrained
                          .gatsbyImageData
                      }
                      alt={
                        website?.secondaryMessage?.hero.description as string
                      }
                    />
                    <span className="sr-only">
                      {website?.secondaryMessage?.heroImageLink.title}
                    </span>
                  </InternalLink>
                ) : (
                  <Image
                    className="rounded-t-lg lg:rounded-l-lg lg:rounded-tr-none"
                    image={
                      website?.secondaryMessage?.heroConstrained.gatsbyImageData
                    }
                    alt={website?.secondaryMessage?.hero.description as string}
                  />
                )}
              </div>
              <div className="p-6 lg:flex lg:flex-col lg:w-1/3">
                <Heading
                  data-cy="secondary-heading"
                  as="h2"
                  className="mb-12 text-2xl lg:mb-6 lg:grow"
                  textAlign="left"
                  fontWeight="bold"
                  textTransform="normal-case"
                >
                  {website?.secondaryMessage.title}
                </Heading>
                <div className="text-center">
                  <InternalLink
                    className="block px-10 py-3 text-base font-bold text-white rounded-full hover:underline leading-tigher bg-[color:var(--cta-bg-color)] hover:text-white"
                    href={
                      website?.secondaryMessage?.heroImageLink?.path ||
                      website?.secondaryMessage.path
                    }
                    aria-label={`${lexicon.readMore}: ${website?.secondaryMessage.title}`}
                  >
                    {lexicon.readMore}
                  </InternalLink>
                </div>
              </div>
            </div>
          </section>
          <Facts facts={website?.secondaryMessage.facts} />
        </>
      )}
      {website?.homepageVideo && <HomeVideo video={website.homepageVideo} />}
      <Certificates certificates={website?.certificates} />
      {/* TODO: Put this into a separate component and use fragment composition */}
      {website?.promotedLinksHeading &&
        website?.promotedLinks &&
        website?.promotedLinks.length > 0 && (
          <section className="max-w-container px-6 mx-auto sm:px-20 lg:px-40">
            <Heading
              as="h2"
              className="py-10 text-2xl md:text-[1.625rem] md:py-14"
              textAlign="left"
            >
              {website?.promotedLinksHeading}
            </Heading>
            <ul className="md:grid md:grid-cols-2 md:gap-x-6">
              {website?.promotedLinks.map((link, i) => (
                <li
                  key={link.id}
                  className={cx(
                    "border-t border-ghost py-3 border-dotted",
                    {
                      "border-b":
                        website?.promotedLinks &&
                        website?.promotedLinks?.length - 1 === i,
                    },
                    {
                      "md:border-b":
                        website?.promotedLinks &&
                        website?.promotedLinks?.length - 2 === i &&
                        website?.promotedLinks?.length % 2 === 0,
                    }
                  )}
                >
                  {link.__typename !== "Hyperlink" && (
                    <InternalLink
                      className="text-base font-bold underline hover:no-underline text-scarpa-flow hover:text-[color:var(--link-color-hover)]"
                      href={link.path}
                    >
                      {link.title}
                    </InternalLink>
                  )}
                  {link.__typename === "Hyperlink" && (
                    <ExternalLink
                      className="text-base font-bold underline hover:no-underline text-scarpa-flow hover:text-[color:var(--link-color-hover)]"
                      href={link.path}
                    >
                      {link.title}
                    </ExternalLink>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      <HomeMessage />
    </Layout>
  );
};

export const query = graphql`
  query HomeQueryV2 {
    allSustainabilityCategory {
      nodes {
        ...HomeWidgetCategory
      }
    }
    website {
      ...homeFields
      id
      dmpAttributes
      path
      messages {
        path
        title
        hero {
          ...PromoCardHeroImage
        }
        teaserImage {
          ...PromoCardTeaserImage
        }
      }
      sustainabilityWidgetWebsite {
        ...HomeWidget
      }
      homepageVideo {
        ...HomeVideoData
      }
      coreMessage {
        heroVideo {
          cdnVideo {
            path
            posterPath
          }
        }
        heroImageLink {
          ... on LandingPage {
            path
            title
          }
          ... on Message {
            path
            title
          }
          ... on SignUpFormWidget {
            path
            title
          }
          ... on Subject {
            path
            title: heading
          }
          ... on Hyperlink {
            path: url
            title
          }
        }
      }
      secondaryMessage {
        ...messageFields
        heroImageLink {
          ... on LandingPage {
            path
            title
          }
          ... on Message {
            path
            title
          }
          ... on SignUpFormWidget {
            path
            title
          }
          ... on Subject {
            path
            title: heading
          }
          ... on Hyperlink {
            path: url
            title
          }
        }
      }
      promotedLinksHeading
      promotedLinks {
        __typename
        ... on ContactUs {
          id
          title
          path
        }
        ... on Faq {
          id
          title
          path
        }
        ... on Hyperlink {
          id
          title
          path: url
        }
        ... on LandingPage {
          id
          title
          path
        }
        ... on Message {
          id
          title
          path
        }
        ... on SignUpFormWidget {
          id
          title
          path
        }
        ... on Sitemap {
          id
          title
          path
        }
        ... on Subject {
          id
          title: navigationTitle
          path
        }
      }
    }
  }
`;

export default Home;

export const Head = ({ data }: Props) => {
  const { website } = data;
  return (
    <GlobalHead
      absolutePath={website?.absolutePath as string}
      home={website as HomeFieldsFragment}
      metaDescription={website?.brand.description as string}
      pageTitle={website?.homepageTitle}
      pageType="home"
      testId="home"
      shareImgSrc={getSrc(website?.coreMessage?.hero.gatsbyImageData)}
      dmpAttributes={website?.dmpAttributes}
      id={website?.id as string}
    />
  );
};
